import { Suspense, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCompanyStore } from "@/common/states/store/Company";
import {
  getPartnerCharges,
  IPartnerCharge,
} from "@/common/services/partner-charge.api";
import { AuthContext } from "@/common/components/auth/authContext/AuthContext";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import TableFooter from "@/common/components/ui/table/TableFooter";

const STATUS_LABELS: Record<string, string> = {
  OPEN: "Aberto",
  CHARGED: "Cobrado",
};

const HEADERS: Record<string, string> = {
  partnerCompanyName: "Parceiro",
  maintenanceCount: "Manutenções",
  preparationCount: "Preparações",
  status: "Status",
  updatedAt: "Última Atualização",
};

const mapDataForTable = (data: IPartnerCharge[]): Record<string, unknown>[] => {
  return data.map((item) => ({
    ...item,
    _original: item,
  })) as unknown as Record<string, unknown>[];
};

export default function TablePartnerCharge() {
  useContext(AuthContext);
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const company = useCompanyStore();

  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-createdAt",
    itemsPerPage: "10",
  });

  const [rows, setRows] = useState<IPartnerCharge[]>([]);
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const actions = [
    {
      label: "Ver Detalhes",
      action: (data: unknown) => {
        const item = data as IPartnerCharge;
        navigate(`/financeiro/cobrancas-parceiros/${item._id}`);
      },
      permission: "view:finance",
    },
  ];

  const loadCharges = useCallback(async () => {
    setIsLoading(true);
    const result = await getPartnerCharges({
      ...filters,
      page: page,
      itemsPerPage: Number(filters.itemsPerPage) || data.itemsPerPage,
    });

    if (!result.ok) {
      notifyError("Não foi possível carregar as cobranças dos parceiros.");
      setIsLoading(false);
      return;
    }

    setData((prevData) => ({
      itemsPerPage: prevData.itemsPerPage,
      totalItems: result.totalItems,
    }));
    setRows(result.data as IPartnerCharge[]);
    setIsLoading(false);
  }, [filters, page, data.itemsPerPage, notifyError]);

  useEffect(() => {
    loadCharges();
  }, [loadCharges, company]);

  const columnRenderers: Record<
    string,
    (val: unknown, row: unknown) => JSX.Element | string
  > = {
    partnerCompanyName: (val: unknown, row: unknown) => {
      const charge = row as IPartnerCharge;
      return (
        <a
          href={`/financeiro/cobrancas-parceiros/${charge._id}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/financeiro/cobrancas-parceiros/${charge._id}`);
          }}
          className="text-bluePrimary hover:underline font-semibold cursor-pointer"
        >
          {String(val)}
        </a>
      );
    },
    maintenanceCount: (val: unknown) => String(val || 0),
    preparationCount: (val: unknown) => String(val || 0),
    status: (value: unknown) => (
      <span
        className={`px-2 py-1 rounded text-xs font-semibold ${
          value === "OPEN"
            ? "bg-yellow-100 text-yellow-800"
            : "bg-green-100 text-green-800"
        }`}
      >
        {STATUS_LABELS[String(value)] || String(value)}
      </span>
    ),
    updatedAt: (_value: unknown, row: unknown) =>
      new Date((row as IPartnerCharge).createdAt).toLocaleDateString("pt-BR"),
  };

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Cobranças de Parceiros"
          placeholder="Digite o nome do parceiro"
          statusOptions={STATUS_LABELS}
          onSearch={handleFilterChange}
          tableData={rows as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          entity="partner-charge"
          headers={HEADERS}
          data={mapDataForTable(rows)}
          actions={actions}
          renders={columnRenderers}
          setFilters={({ orderBy }) => setFilters((f) => ({ ...f, orderBy }))}
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 0}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}
